// ------------------------------------------------------------
// Alert Message Dialog Component
// ------------------------------------------------------------
// This component displays a modal dialog for showing alert or
// confirmation messages. It supports both success and error states,
// customizable actions, and conditional close behavior.
//
// Features included:
// - Success/Error states with icons (CheckCircle, AlertTriangle)
// - Customizable title, message, and confirmation button
// - Optional close behavior (canClose toggle)
// ------------------------------------------------------------

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { AlertTriangle, CheckCircle } from "lucide-react";
import { Success_Icon, Alerts_Icon, Alerts_Fail } from "@/lib/Images";

/**
 * AlertMsg
 * ------------------------------------------------------------
 * Renders a modal dialog for alerts or confirmations.
 *
 * @param {Object} props - Component props.
 * @param {string} props.label - Title of the dialog.
 * @param {string} props.msg - Description or message body.
 * @param {boolean} props.open - Controls whether the dialog is visible.
 * @param {Function} props.onClose - Callback triggered when dialog closes.
 * @param {Function} props.confirmAction - Action executed when confirm button is clicked.
 * @param {boolean} props.isLoading - Loading state for confirm button (future use).
 * @param {boolean} props.isError - Determines whether dialog is error or success.
 * @param {boolean} [props.canClose=true] - Whether dialog can be closed manually.
 * @param {string} props.confirmationBtnText - Label for the confirmation button.
 *
 * @returns {JSX.Element} - Alert/confirmation dialog.
 */
export function AlertMsg({
  label,
  msg,
  open,
  onClose,
  confirmAction,
  isLoading,
  isError,
  canClose = true,
  confirmationBtnText,
}) {
  return (
    <Dialog
      open={open}
      onOpenChange={() => {
        canClose && onClose();
      }}
    >
      <DialogContent
        className="sm:max-w-lg !p-10 bg-brand-10 border-brand-11"
        canClose={canClose}
      >
        {/* Dialog Header with icon + title + description */}
        <DialogHeader className="text-center">
          {isError ? (
            <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-red-100 flex items-center justify-center">
              <AlertTriangle className="h-8 w-8 text-red-600" />
            </div>
          ) : (
            <div className="mx-auto mb-4 h-20 w-20 rounded-full  flex items-center justify-center">
              <img
                src={Success_Icon} // your image path here
                alt="Success"
                className="h-18 w-18"
              />
            </div>
          )}

          <DialogTitle className="text-2xl font-bold text-center">
            {label}
          </DialogTitle>
          <DialogDescription className="text-[16px] text-center text-foreground !mt-4">
            {msg}
          </DialogDescription>
        </DialogHeader>

        {/* Dialog Footer with confirmation button */}
        <DialogFooter className="flex gap-3 sm:gap-3">
          {/* Optional cancel button (can be re-enabled if needed)
          <Button variant="outline" onClick={onClose} className="flex-1">
            Cancel
          </Button>
          */}
          <div className="flex flex-col justify-center w-[50%] m-auto !mt-4">
            <Button
              onClick={confirmAction}
              variant=""
              className="flex-1 bg-primary"
              // disabled={isLoading} // Future: disable button when loading
            >
              {confirmationBtnText}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default AlertMsg;
